import React from 'react';
import './AnalyticsCard.css';

interface AnalyticsCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: 'users' | 'chat' | 'message' | 'activity' | 'trends' | 'clock';
  color: string;
  trend?: number;
  format?: 'number' | 'percentage' | 'currency';
  size?: 'small' | 'medium' | 'large';
}

const AnalyticsCard: React.FC<AnalyticsCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  trend,
  format = 'number',
  size = 'medium'
}) => {
  const formatValue = (val: number) => {
    switch (format) {
      case 'percentage':
        return `${val.toFixed(1)}%`;
      case 'currency':
        return new Intl.NumberFormat('es-CO', { 
          style: 'currency', 
          currency: 'COP',
          minimumFractionDigits: 0
        }).format(val);
      default:
        return val.toLocaleString('es-ES');
    }
  };

  const getTrendIcon = () => {
    if (trend === undefined || trend === 0) return null;
    
    return trend > 0 ? (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
        <polyline points="17 6 23 6 23 12"></polyline>
      </svg>
    ) : (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
        <polyline points="17 18 23 18 23 12"></polyline>
      </svg>
    );
  };

  const getIcon = () => {
    const iconProps = {
      width: size === 'small' ? 20 : size === 'large' ? 28 : 24,
      height: size === 'small' ? 20 : size === 'large' ? 28 : 24,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2"
    };

    switch (icon) {
      case 'users':
        return (
          <svg {...iconProps}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        );
      case 'chat':
        return (
          <svg {...iconProps}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        );
      case 'message':
        return (
          <svg {...iconProps}>
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22,2 15,22 11,13 2,9 22,2"></polygon>
          </svg>
        );
      case 'activity':
        return (
          <svg {...iconProps}>
            <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"></polyline>
          </svg>
        );
      case 'trends':
        return (
          <svg {...iconProps}>
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
            <polyline points="17 6 23 6 23 12"></polyline>
          </svg>
        );
      case 'clock':
        return (
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12,6 12,12 16,14"></polyline>
          </svg>
        );
      default:
        return (
          <svg {...iconProps}>
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          </svg>
        );
    }
  };

  return (
    <div className={`analytics-card ${size}`}>
      <div className="card-header">
        <div className="card-icon" style={{ color }}>
          {getIcon()}
        </div>
        <div className="card-trend">
          {trend !== undefined && (
            <span className={`trend-indicator ${trend >= 0 ? 'positive' : 'negative'}`}>
              {getTrendIcon()}
              {Math.abs(trend)}
            </span>
          )}
        </div>
      </div>
      
      <div className="card-content">
        <h3 className="card-title">{title}</h3>
        <div className="card-value" style={{ color }}>
          {formatValue(value)}
        </div>
        {subtitle && (
          <p className="card-subtitle">{subtitle}</p>
        )}
      </div>

      {/* Decorative background */}
      <div className="card-background" style={{ backgroundColor: `${color}08` }}>
        <div className="background-icon" style={{ color: `${color}20` }}>
          {getIcon()}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsCard;